<template>
  <div id="DistributorSearchPage">
    <distributor-search-filter
      class="distributor-search-filter"
      @address="handleAddress"
      @reset="reset"
    />
    <client-only>
      <distributor-search-results
        class="distributor-search-results"
        :distributors="distributors"
        :focussed-distributor="focussedDistributor"
        :loading="loading"
        @focusOnDistributor="handleFocusOnDistributor"
      />
    </client-only>
    <distributor-search-map
      class="distributor-search-map"
      :distributors="distributors"
      :points="pointsLocal"
      :focussed-distributor="focussedDistributor ?? {}"
      :distributor-selected="selectedDistributor != null"
      @marker-closed="handleMarkerClosed"
      @marker-opened="handleMarkerOpened"
      ref="map"
      :refresh-id="refreshId"
      :local-atlas="atlasLocal"
      :local-map="mapLocal"
      :local-map-client="map_client"
    />
  </div>
</template>

<script setup lang="ts">
import DistributorSearchFilter from '~/components/distributor-search/DistributorSearchFilter.vue'
import DistributorSearchMap from '~/components/distributor-search/DistributorSearchMap.vue'
import {useDistributorsService} from '@/composables/distributors_service'
import DistributorSearchResults from '~/components/distributor-search/DistributorSearchResults'
import {isUnexpected} from '@azure-rest/maps-search'
import useI18nExtended from '~/composables/usei18nExtended'
import {useCanonicalPathHelper} from '~/composables/canonical_path_helper'
import {useAzureAtlas} from '~/composables/useAzureAtlas'

const route = useRoute()
const config = useRuntimeConfig()
const i18n = useI18nExtended()
const params = route.params
const router = useRouter()
const {getDistributorById, getDistributors} = useDistributorsService()
const {adjustCanonicalPath} = useCanonicalPathHelper()
const azureAtlas = useAzureAtlas()

let loadedAfterReset = route?.query?.reset ?? false

let distributorIdLocal
if (params?.distributor) {
  distributorIdLocal = parseInt(/(\d+)[-_].*/.exec(params?.distributor)?.[1], 10)
  if (!distributorIdLocal) {
    await navigateTo(/(.*)\/[^/]+/.exec(route.path)[1])
  }
}

// const regions: any[] = []
const path = route.path
let i18nHead = useLocaleHead({addSeoAttributes: true, route: route, router: router, i18n: i18n}).value
adjustCanonicalPath(path, i18nHead)

const loading = ref(true)
let distributors: any = ref([])
let atlasLocal: any = ref(null)
let mapLocal: any = ref(null)
let map_client: any = null
let refreshId = ref(-1)
let pointsLocal: any = ref([])
let searchedSelectedDistOnce = false
let geolocationFromUser: any = null
const selectedDistributor = ref(null)
const focussedDistributor = ref(null)
// const listOfPartners = ref([])

let distributorId: any = ref(null)
let atlasLoadErrorShown = false
const mapInitAttempts = ref(0)
const mapInitMax = 6
const reloadGuardKey = 'atlasReloadAttempted'

distributorId.value = distributorIdLocal ? Number(distributorIdLocal) : null

useHead({
  htmlAttrs: {
    ...i18nHead.htmlAttrs
  },
  meta: [
    ...i18nHead.meta
  ],
  link: [
    ...i18nHead.link,
    {rel: 'stylesheet', type: 'text/css', href: 'https://atlas.microsoft.com/sdk/javascript/mapcontrol/2/atlas.min.css'}
  ]
})

/** TODOS
 * distributor-search/1818_rubix - such links not working yet, missing routing
 * translations missing  for prerendered parts- check after complete i18n implementation if layout is fine
 */
// eslint-disable-next-line no-unused-vars
const distributor = computed(() => {
  return route.params.distributor ? String(route.params.distributor) : null
})

useBreadcrumb([{label: i18n.tv('distributorsearch', 'breadcrumbs')}])

onMounted(async () => {
  // Request geolocation - browser will prompt user if permission not granted
  if ('geolocation' in navigator && !loadedAfterReset) {
    await new Promise<void>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          geolocationFromUser = position?.coords ?? null
          resolve()
        },
        () => {
          resolve()
        }
      )
    })
  }

  if (config.public.AZURE_MAPS_API_KEY) {
    await initAtlasMapWithRetry()
  }
  await getDistributorsLocal()
})

onUnmounted(() => {
  mapLocal.value?.dispose()
  mapLocal.value = null
  mapInitAttempts.value = 0
  try {
    sessionStorage.removeItem(reloadGuardKey)
  } catch {
    // ignore
  }
})

const waitForAtlas = async (remainingMs = 20000): Promise<void> => {
  const poll = async (timeLeft: number): Promise<void> => {
    const atlasPresent = globalThis.atlas !== undefined
    const markerLayerPresent = globalThis.atlas?.layer?.HtmlMarkerLayer
    const azureAtlasDiv = document?.getElementById('AzureAtlas')

    if (atlasPresent && markerLayerPresent && azureAtlasDiv) {
      return
    }

    if (timeLeft <= 0) {
      throw new Error('Atlas stack not available after waiting')
    }

    await new Promise(res => setTimeout(res, 250))
    return poll(timeLeft - 250)
  }

  return poll(remainingMs)
}

const scheduleControlledReload = () => {
  try {
    if (sessionStorage.getItem(reloadGuardKey) === '1') {
      return
    }
    sessionStorage.setItem(reloadGuardKey, '1')
    setTimeout(() => {
      globalThis.location.reload()
    }, 300)
  } catch {
    // ignore
  }
}

const initAtlasMapWithRetry = async () => {
  if (mapInitAttempts.value >= mapInitMax) {
    if (!atlasLoadErrorShown) {
      const toast = useNuxtApp().$toast as any
      toast?.error(i18n.tv('mapDataNotAvailable', 'distributorSearch'))
      atlasLoadErrorShown = true
    }
    scheduleControlledReload()
    return
  }

  mapInitAttempts.value += 1
  try {
    await azureAtlas.ensureAtlasStack(config.public.AZURE_MAPS_API_KEY)
    await waitForAtlas()
    const atlasGlobal = globalThis.atlas
    if (!atlasGlobal?.layer?.HtmlMarkerLayer) {
      throw new Error('Atlas stack missing after load')
    }
    atlasLocal.value = atlasGlobal
    mapInitAttempts.value = 0

    mapLocal.value = new atlasLocal.value.Map('AzureAtlas', {
      authOptions: {
        authType: 'subscriptionKey',
        subscriptionKey: config.public.AZURE_MAPS_API_KEY
      },
      view: 'auto',
      language: i18n.locale.value || 'en'
    })
    mapLocal.value.setStyle({
      renderWorldCopies: true,
      showBuildingModels: true,
      showLogo: false,
      showFeedbackLink: false,
      style: 'road_shaded_relief'
    })

    mapLocal.value.controls.add(new atlasLocal.value.control.ZoomControl(), {
      position: 'bottom-right'
    })

    map_client = azureAtlas.mapClient
    refreshId.value += 1
  } catch (e: any) {
    // Don't log individual retry attempts - only log final failure
    if (mapInitAttempts.value < mapInitMax) {
      const delay = mapInitAttempts.value <= 10 ? 500 : Math.min(500 + (mapInitAttempts.value - 10) * 950, 10000)
      await new Promise(res => setTimeout(res, delay))
      return initAtlasMapWithRetry()
    }
    if (!atlasLoadErrorShown) {
      const toast = useNuxtApp().$toast as any
      toast?.error(i18n.tv('mapDataNotAvailable', 'distributorSearch'))
      atlasLoadErrorShown = true
    }
    scheduleControlledReload()
  }
}

const getDistributorsLocal = async (lng =  null, lat = null, classification = null) => {
  loading.value = true
  // eslint-disable-next-line eqeqeq
  if (distributorId.value != null && !searchedSelectedDistOnce && !loadedAfterReset) {
    const distributorLocal = await getDistributorById(distributorId.value)
    if (distributorLocal && distributorLocal?.length > 0) {
      selectedDistributor.value = distributorLocal[0]
      focussedDistributor.value = distributorLocal[0]
    }
    searchedSelectedDistOnce = true
  }
  let lngValue, latValue
  if (!loadedAfterReset) {
    if (selectedDistributor.value?.gps?.lng && selectedDistributor.value?.gps?.lat) {
      lngValue = selectedDistributor.value?.gps?.lng
      latValue = selectedDistributor.value?.gps?.lat
      geolocationFromUser = null
    } else if (geolocationFromUser !== null) {
      lngValue = geolocationFromUser?.longitude
      latValue = geolocationFromUser?.latitude
      geolocationFromUser = null
    } else {
      lngValue = lng
      latValue = lat
    }
  }
  loadedAfterReset = false
  const res = await getDistributors(lngValue, latValue, classification)
  distributors.value = res.distributors

  if (selectedDistributor.value?.gps) {
    const {lngDist, latDist} = selectedDistributor.value.gps
    pointsLocal.value = [{lng: lngDist, lat: latDist}]
  } else if (lngValue && latValue) {
    focussedDistributor.value = distributors.value[0]
    const newPoints = [{lng: lngValue, lat: latValue}]
    if (distributors.value[0]?.gps) {
      newPoints.push({lng: distributors.value[0].gps.lng, lat: distributors.value[0].gps.lat})
    }
    pointsLocal.value = newPoints
  }

  loading.value = false
}

const handleAddress = async (payload: any) => {
  const {search, classification} = payload
  selectedDistributor.value = null
  const points = []

  if (search !== '') {
    if (!map_client) {
      // Client not available - likely due to initialization failure already logged
      loading.value = false
      return
    }
    try {
      const response = await map_client.path('/search/address/{format}', 'json')
        .get({queryParameters: {query: search}})

      if (response && isUnexpected(response)) {
        // Unexpected response - API may be temporarily unavailable
      // eslint-disable-next-line eqeqeq
      } else if (response?.status == 200) {
        const results = response?.body?.results

        if (results) {
          const lng = results[0]?.position?.lon
          const lat = results[0]?.position?.lat

          if (lng && lat) {
            points.push({lng, lat})
            await getDistributorsLocal(lng, lat, classification)
            if (distributors.value.length > 0) {
              const nearest = distributors.value[0]
              if (nearest?.gps?.lng && nearest.gps.lat) {
                points.push({lng: nearest.gps.lng, lat: nearest.gps.lat})
                focussedDistributor.value = nearest
              }
            }
          }
        }
      }
    } catch (e) {
      // Address search failed - could be network issue, invalid input, or rate limiting
      // User will see no results; no need to log as it's often transient or user error
    }
  } else {
    await getDistributorsLocal(null, null, classification)
  }
  pointsLocal.value = points
}

const handleFocusOnDistributor = (distributor: any) => {
  if (distributor?.gps) {
    pointsLocal.value = [{lng: distributor.gps.lng, lat: distributor.gps.lat}]
    focussedDistributor.value = distributor
  }
}

const handleMarkerClosed = (distributor: any) => {
  if (distributor && focussedDistributor.value?.Id === distributor.Id) {
    focussedDistributor.value = null
  }
}

const handleMarkerOpened = (distributor: any) => {
  if (distributor.Id !== focussedDistributor.value?.Id) {
    focussedDistributor.value = distributor
  }
}
const reset = () => {
  const el = document.getElementById('DistributorSearchResultList')
  if (el) {
    el.scroll({top: 0, behavior: 'smooth'})
  }
  searchedSelectedDistOnce = false
  selectedDistributor.value = null
  focussedDistributor.value = null
  geolocationFromUser = null
  pointsLocal.value = []
  if (distributorId.value) {
    router.push(useNuxtApp().$globalization.localePath('distributor-search', null, null) + '?reset')
  } else {
    router.push(useNuxtApp().$globalization.localePath('distributor-search', null, null))
    getDistributorsLocal()
  }
}
</script>

<style lang="scss">
[id='DistributorSearchPage'] {
  padding-bottom: 0;
  margin-bottom: 14.5rem;
  min-height: 54rem;
  position: relative;
  display: flex;
  flex-direction: column;

  .maploader {
    position: absolute;
    display: flex;
    inset: 0;
    justify-content: center;
    align-items: center;
  }

  @include breakpoint-down($md) {
    flex-flow: wrap;
  }

  // Filters
  .distributor-search-filter {
    order: 1;
  }

  // Search Resultst
  .distributor-search-results {
    order: 2;
    min-height: 40rem;
    background-color: $color__content--background__secondary;
    width: 24.75rem;
    padding: $default-margin-padding-width $default-margin-padding-width $default-margin-padding-width $default-margin-padding-2x;
  }

  // Map
  .distributor-search-map {
    order: 3;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    width: calc(100% - 27.75rem);
  }
}

@include breakpoint-down($md) {
  [id='DistributorSearchPage'] {
    // Filters
    .distributor-search-filter {
      order: 1;
      width: 100%;
    }

    // Search Resultst
    .distributor-search-results {
      order: 3;
      width: 100%;
      padding: $default-margin-padding-width;
    }

    // Map
    .distributor-search-map {
      order: 2;
      width: 100%;
      position: relative;
    }
  }
}
</style>
